import { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useRoleAssumption, ROLE_LABELS } from '../../context/RoleAssumptionContext';
import RoleSwitcher from './RoleSwitcher';
import './Header.css';

interface NavItem {
  path: string;
  labelKey: string;
}

const NAV_ITEMS: NavItem[] = [
  { path: '/', labelKey: 'nav.home' },
  { path: '/ratings', labelKey: 'nav.ratings' },
  { path: '/reports', labelKey: 'nav.reports' },
  { path: '/analyst-bench', labelKey: 'nav.bench' },
  { path: '/agents', labelKey: 'nav.agents' },
];

export default function Header() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const { effectiveRole, isAssuming } = useRoleAssumption();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node)) {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close mobile menu on escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const handleNavClick = (path: string) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  // Get current page label for mobile menu button
  const activeItem = NAV_ITEMS.find(item => isActive(item.path));
  const currentPageLabel = activeItem ? t(activeItem.labelKey) : t('nav.menu');

  return (
    <header className="header">
      <div className="header-container">
        {/* Logo */}
        <Link to="/" className="header-logo">
          <span className="logo-text">BankingLATAM</span>
        </Link>

        {/* Mobile Navigation Menu */}
        <div className="mobile-nav-wrapper" ref={mobileMenuRef}>
          <button
            className="mobile-nav-trigger"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-expanded={mobileMenuOpen}
            aria-haspopup="menu"
            aria-label={t('nav.menu')}
          >
            <svg
              className="mobile-nav-hamburger"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              {mobileMenuOpen ? (
                <>
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </>
              ) : (
                <>
                  <line x1="3" y1="12" x2="21" y2="12" />
                  <line x1="3" y1="6" x2="21" y2="6" />
                  <line x1="3" y1="18" x2="21" y2="18" />
                </>
              )}
            </svg>
            <span className="mobile-nav-current">{currentPageLabel}</span>
            <svg
              className={`mobile-nav-chevron ${mobileMenuOpen ? 'open' : ''}`}
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>

          {mobileMenuOpen && (
            <div className="mobile-nav-dropdown" role="menu">
              {NAV_ITEMS.map((item) => (
                <button
                  key={item.path}
                  className={`mobile-nav-item ${isActive(item.path) ? 'active' : ''}`}
                  onClick={() => handleNavClick(item.path)}
                  role="menuitem"
                >
                  <span className="mobile-nav-label">{t(item.labelKey)}</span>
                  {isActive(item.path) && (
                    <svg
                      className="mobile-nav-check"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                  )}
                </button>
              ))}
              <div className="mobile-nav-divider" />
              <button
                className={`mobile-nav-item ${isActive('/settings') ? 'active' : ''}`}
                onClick={() => handleNavClick('/settings')}
                role="menuitem"
              >
                <span className="mobile-nav-label">{t('nav.settings')}</span>
              </button>
            </div>
          )}
        </div>

        {/* Desktop Navigation */}
        <nav className="header-nav desktop-nav">
          {NAV_ITEMS.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`header-nav-item ${isActive(item.path) ? 'active' : ''}`}
            >
              {t(item.labelKey)}
            </Link>
          ))}
        </nav>

        {/* Right Section - Settings and Profile */}
        <div className="header-right">
          {/* Role Switcher - only for Platform Admins */}
          <RoleSwitcher />

          {/* Settings - desktop only */}
          <Link
            to="/settings"
            className={`header-icon-btn desktop-only ${isActive('/settings') ? 'active' : ''}`}
            aria-label={t('nav.settings')}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M10 12.5C11.3807 12.5 12.5 11.3807 12.5 10C12.5 8.61929 11.3807 7.5 10 7.5C8.61929 7.5 7.5 8.61929 7.5 10C7.5 11.3807 8.61929 12.5 10 12.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M16.1667 12.5C16.0557 12.7513 16.0226 13.0302 16.0716 13.3005C16.1206 13.5708 16.2495 13.8203 16.4417 14.0167L16.4917 14.0667C16.6466 14.2215 16.7695 14.4053 16.8534 14.6076C16.9373 14.8099 16.9805 15.0268 16.9805 15.2458C16.9805 15.4649 16.9373 15.6817 16.8534 15.884C16.7695 16.0863 16.6466 16.2702 16.4917 16.425C16.3369 16.5799 16.153 16.7028 15.9507 16.7867C15.7484 16.8706 15.5316 16.9138 15.3125 16.9138C15.0934 16.9138 14.8766 16.8706 14.6743 16.7867C14.472 16.7028 14.2881 16.5799 14.1333 16.425L14.0833 16.375C13.887 16.1828 13.6375 16.0539 13.3672 16.0049C13.0969 15.9559 12.818 15.989 12.5667 16.1C12.3203 16.2056 12.1124 16.3857 11.9704 16.6154C11.8284 16.845 11.7591 17.1134 11.7717 17.3842L11.7717 17.5C11.7717 17.942 11.596 18.3659 11.2834 18.6785C10.9709 18.9911 10.547 19.1667 10.105 19.1667C9.66296 19.1667 9.23905 18.9911 8.92649 18.6785C8.61393 18.3659 8.43833 17.942 8.43833 17.5L8.43833 17.425C8.42356 17.1469 8.34232 16.8771 8.19167 16.6474C8.04101 16.4177 7.82587 16.2368 7.575 16.1292C7.3237 16.0181 7.04479 15.9851 6.77448 16.0341C6.50416 16.0831 6.25464 16.2119 6.05833 16.4042L6.00833 16.4542C5.85349 16.609 5.66964 16.7319 5.46734 16.8158C5.26503 16.8998 5.04817 16.9429 4.82917 16.9429C4.61016 16.9429 4.3933 16.8998 4.191 16.8158C3.98869 16.7319 3.80484 16.609 3.65 16.4542C3.49518 16.2993 3.37225 16.1155 3.28837 15.9132C3.20449 15.7109 3.16132 15.494 3.16132 15.275C3.16132 15.056 3.20449 14.8391 3.28837 14.6368C3.37225 14.4345 3.49518 14.2507 3.65 14.0958L3.7 14.0458C3.89218 13.8495 4.02107 13.6 4.07006 13.3297C4.11906 13.0594 4.08602 12.7805 3.975 12.5292C3.86938 12.2828 3.68938 12.0749 3.45971 11.9329C3.23003 11.7909 2.96167 11.7216 2.69083 11.7342L2.575 11.7342C2.13296 11.7342 1.70905 11.5586 1.39649 11.246C1.08393 10.9335 0.908325 10.5095 0.908325 10.0675C0.908325 9.62551 1.08393 9.2016 1.39649 8.88904C1.70905 8.57648 2.13296 8.40088 2.575 8.40088L2.65 8.40088C2.92816 8.38611 3.19793 8.30487 3.42764 8.15422C3.65735 8.00356 3.8383 7.78843 3.94583 7.53755C4.05686 7.28626 4.08989 7.00735 4.0409 6.73703C3.9919 6.46672 3.86301 6.2172 3.67083 6.02088L3.62083 5.97088C3.46602 5.81604 3.34308 5.63219 3.2592 5.42989C3.17532 5.22758 3.13216 5.01072 3.13216 4.79172C3.13216 4.57271 3.17532 4.35585 3.2592 4.15355C3.34308 3.95124 3.46602 3.7674 3.62083 3.61255C3.77568 3.45774 3.95952 3.3348 4.16183 3.25092C4.36413 3.16704 4.58099 3.12388 4.8 3.12388C5.01901 3.12388 5.23587 3.16704 5.43817 3.25092C5.64048 3.3348 5.82432 3.45774 5.97917 3.61255L6.02917 3.66255C6.22548 3.85474 6.475 3.98363 6.74531 4.03262C7.01563 4.08162 7.29454 4.04858 7.54583 3.93755H7.60417C7.85053 3.83194 8.05847 3.65194 8.20045 3.42226C8.34244 3.19259 8.41175 2.92422 8.39917 2.65338L8.39917 2.5C8.39917 2.05797 8.57477 1.63405 8.88733 1.32149C9.19989 1.00893 9.6238 0.833328 10.0658 0.833328C10.5079 0.833328 10.9318 1.00893 11.2443 1.32149C11.5569 1.63405 11.7325 2.05797 11.7325 2.5L11.7325 2.575C11.7199 2.84584 11.7892 3.11421 11.9312 3.34388C12.0732 3.57356 12.2811 3.75356 12.5275 3.85917C12.7788 3.9702 13.0577 4.00323 13.328 3.95424C13.5983 3.90524 13.8479 3.77636 14.0442 3.58417L14.0942 3.53417C14.249 3.37936 14.4328 3.25642 14.6351 3.17254C14.8375 3.08866 15.0543 3.0455 15.2733 3.0455C15.4923 3.0455 15.7092 3.08866 15.9115 3.17254C16.1138 3.25642 16.2976 3.37936 16.4525 3.53417C16.6073 3.68902 16.7302 3.87286 16.8141 4.07517C16.898 4.27747 16.9412 4.49433 16.9412 4.71334C16.9412 4.93234 16.898 5.1492 16.8141 5.35151C16.7302 5.55381 16.6073 5.73766 16.4525 5.8925L16.4025 5.9425C16.2103 6.13882 16.0814 6.38834 16.0324 6.65865C15.9834 6.92897 16.0165 7.20788 16.1275 7.45917V7.51751C16.2331 7.76387 16.4131 7.9718 16.6428 8.11379C16.8725 8.25577 17.1408 8.32509 17.4117 8.31251H17.5C17.942 8.31251 18.366 8.48811 18.6785 8.80067C18.9911 9.11323 19.1667 9.53714 19.1667 9.97917C19.1667 10.4212 18.9911 10.8451 18.6785 11.1577C18.366 11.4703 17.942 11.6459 17.5 11.6459H17.425C17.1542 11.6584 16.8858 11.7277 16.6561 11.8697C16.4265 12.0117 16.2465 12.2196 16.1408 12.466L16.1667 12.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </Link>

          {/* Profile */}
          <div
            className="header-profile"
            onClick={() => navigate('/profile')}
            role="button"
            tabIndex={0}
            aria-label="User profile"
            onKeyDown={(e) => e.key === 'Enter' && navigate('/profile')}
          >
            <div className="profile-info">
              <div className="profile-name">
                {userProfile?.display_name || 'User'}
              </div>
              <div className={`profile-role ${isAssuming ? 'assuming' : ''}`}>
                {effectiveRole ? t(ROLE_LABELS[effectiveRole]) : t('roles.pending')}
              </div>
            </div>
            {userProfile?.photo_url ? (
              <img
                src={userProfile.photo_url}
                alt={userProfile.display_name}
                className="profile-avatar profile-avatar-img"
              />
            ) : (
              <div className="profile-avatar">
                <span>{getInitials(userProfile?.display_name || 'U')}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map(part => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}
